import React, { createRef, useCallback, useMemo, useState } from 'react';
import {
    Box, Tabs, Tab, RadioGroup, FormControlLabel, Radio,
    TextField, Table, TableRow, TableCell, Grid,
} from '@mui/material';
import { BSItems, PLItems } from '../../constants';
import SaveConditionModal from '../condition/SaveCondition';
import LoadConditionModal from '../condition/LoadCondition';
import styled from '@emotion/styled';
import { autoCalcBS } from './AutoCalcBS';
import { autoCalcPL } from './AutoCalcPL';
import { financialDataState } from './FinancialDataState';
import { handleKeyDown } from './TableKeyboardHandler';

interface ownProps {
    inputState: financialDataState
    yearEdition: any,
    setState: (state: financialDataState) => void,
}

const FinancialDataInputBox = styled(Box)(({ }) => ({
    backgroundColor: '#F2F2F2',
    margin: '0 auto',
    marginBottom: '1rem'
}));

const TabArea = styled(Box)(({ }) => ({
    width: '90%',
    margin: '0 auto',
}));

const DateInputForm = styled(TextField)(({ }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderRadius: 0,
            borderColor: '#000000'
        }
    },
}))

const DataInputTab = styled(Tab)(({ }) => ({
    backgroundColor: '#D9D9D9',
    color: '#979797',
    fontWeight: 'bold',
    padding: '0',
    width: '8rem',
}));

const DataInputArea = styled(Box)(({ }) => ({
    backgroundColor: '#FFFFFF',
    width: '90%',
    margin: '0 auto'
}));

const DataInputTableCell = styled(TableCell)(({ }) => ({
    width: '1000rem',
}));

const DataInputForm = styled.input`
    text-align: right;
    outline: none;
    border: none;
    width: 100%;
    height: 100%;
    padding: 0;
    &:focus {
        background-color: #84F9A9
    };
`

const initState: financialDataState = {
    "activeTab": "BS",
    "tableValue": {
        "fiscalYM1": {
            "BS": {},
            "PL": {}
        },
        "fiscalYM2": {
            "BS": {},
            "PL": {},
        },
        "fiscalYM3": {
            "BS": {},
            "PL": {},
        }
    }
}

const FinancialDataInputForm = (props: ownProps) => {
    const [activeTab, setActiveTab] = useState("BS");
    const [selected,] = useState('fiscalYM1');
    const [iniitFlg, setInitFlg] = React.useState(true);
    const items = activeTab === "BS" ? BSItems : PLItems;
    const [state, setState] = React.useState(initState)
    const [tableValue, setTableValue] = React.useState(state.tableValue)

    const [openSave, setOpenSave] = React.useState(false);
    const [openLoad, setOpenLoad] = React.useState(false);
    const handleOpenSave = () => setOpenSave(true);
    const handleOpenLoad = () => setOpenLoad(true);
    const handleDataLoad = (tableValue: { [key: string]: any }) => {
        setTableValue(tableValue)
        setOpenLoad(false)
    }
    const handleChangeRadio = ((v: string) => {
        const newTableValue = structuredClone(tableValue);
        newTableValue["fiscalYM1"]["isComparisonTarget"] = false
        newTableValue["fiscalYM2"]["isComparisonTarget"] = false
        newTableValue["fiscalYM3"]["isComparisonTarget"] = false
        newTableValue[v]["isComparisonTarget"] = true
        setTableValue(newTableValue)
    });

    React.useEffect(() => {
        window.scrollTo(0, 0)
        let initState: financialDataState = {
            activeTab: '',
            tableValue: {}
        }
        if (props.inputState != undefined) {
            setActiveTab(props.inputState.activeTab)
            initState = props.inputState
        } else {
            // 初期設定可能なstateがない場合は全てのセルに初期値を反映する
            initState["activeTab"] = "BS"
            initState["tableValue"] = {
                "fiscalYM1": {
                    "fiscalYear": "",
                    "fiscalMonth": "",
                    "isComparisonTarget": true,
                    "BS": getItemMap("BS"),
                    "PL": getItemMap("PL")
                },
                "fiscalYM2": {
                    "fiscalYear": "",
                    "fiscalMonth": "",
                    "isComparisonTarget": false,
                    "BS": getItemMap("BS"),
                    "PL": getItemMap("PL")
                },
                "fiscalYM3": {
                    "fiscalYear": "",
                    "fiscalMonth": "",
                    "isComparisonTarget": false,
                    "BS": getItemMap("BS"),
                    "PL": getItemMap("PL")
                }
            }
        }
        setState(initState)
        setTableValue(initState["tableValue"])
        setInitFlg(false)
    }, [])

    const getItemMap = ((type: string) => {
        const items = type === "BS" ? BSItems : PLItems
        const returnMap: { [key: string]: number } = {}
        items.forEach((e) => returnMap[e.key] = 0)
        return returnMap
    })

    const handleChangeFiscalYear = ((fiscalYM: string, v: string) => {
        // 変更予定の値に数値以外が含まれる、または桁数が4桁を超過する場合は値を更新しない
        if ((!(/^\d+$/.test(v)) || v.length > 4) && v != "") {
            return
        }
        const newTableValue = structuredClone(tableValue);
        newTableValue[fiscalYM]["fiscalYear"] = v
        setTableValue(newTableValue)
    })

    const handleChangeFiscalMonth = ((fiscalYM: string, v: string) => {
        // 変更予定の値に数値以外が含まれる、または桁数が2桁を超過する場合は値を更新しない
        if ((!(/^\d+$/.test(v)) || v.length > 2) && v != "") {
            return
        }
        const newTableValue = structuredClone(tableValue);
        newTableValue[fiscalYM]["fiscalMonth"] = v
        setTableValue(newTableValue)
    })

    const handleTableChange = useCallback((fiscalYM: string, key: string, v: string) => {
        const newTableValue = structuredClone(tableValue);
        let changeValue: number = 0

        // -のみの場合は数値にせずそのまま値を反映する
        if (v === '-' || v === '0') {
            newTableValue[fiscalYM][activeTab][key] = v
        } else {
            // マイナスの上に「-」が入力されたら正の数にする
            if (v.match(/-/g)?.length === 2) {
                v = v.replace(/-/g, '');
            } else {
                // 「-」が入力されてきた場合は先頭に移動
                if (v.includes('-')) {
                    v = v.replace('-', '').replace(/^/, '-');
                }
            }
            changeValue = Number(v.replace(/,/g, ''))
            // 変更予定の値に数値以外が含まれる、または符号を除いた桁数が8桁を超過する場合は値を更新しない
            if (!Number.isInteger(changeValue) || v.replace(/,/g, '').replace(/-/g, '').length > 8) {
                return
            }
            newTableValue[fiscalYM][activeTab][key] = changeValue
            // 連動項目の更新
            if (activeTab === "BS") {
                autoCalcBS(newTableValue[fiscalYM][activeTab], key)
            } else {
                autoCalcPL(newTableValue[fiscalYM][activeTab], key)
            }
        }
        setTableValue(newTableValue)
    }, [tableValue["fiscalYM1"][activeTab], tableValue["fiscalYM2"][activeTab], tableValue["fiscalYM3"][activeTab],])

    const handleBlur = useCallback((fiscalYM: string, key: string, v: string) => {
        const newTableValue = structuredClone(tableValue);
        let changeValue = 0
        if (v === '-' || v === '0') {
            changeValue = 0
            newTableValue[fiscalYM][activeTab][key] = changeValue
            // 連動項目の更新
            if (activeTab === "BS") {
                autoCalcBS(newTableValue[fiscalYM][activeTab], key)
            } else {
                autoCalcPL(newTableValue[fiscalYM][activeTab], key)
            }
            setTableValue(newTableValue)
        }
    }, [tableValue["fiscalYM1"][activeTab], tableValue["fiscalYM2"][activeTab], tableValue["fiscalYM3"][activeTab],])

    const [focusCell, setFocusCell] = React.useState<[number, number] | null>([0, 0]);
    const cellRefs = useMemo(() =>
        Array.from({ length: items.length }, () =>
            Array.from({ length: 3 }, () => createRef<HTMLInputElement>())
        ), [items.length]);

    React.useEffect(() => {
        if (!iniitFlg && focusCell != null) {
            cellRefs[focusCell[0]][focusCell[1]].current?.select()
        }
    }, [focusCell])

    React.useEffect(() => {
        if (!iniitFlg) {
            setState(prev => ({ ...prev, activeTab }))
        }
    }, [activeTab])

    React.useEffect(() => {
        setState(prev => ({ ...prev, tableValue }))
    }, [tableValue])

    React.useEffect(() => {
        props.setState(state)
    }, [state])

    const handleFocus = (row: number, col: number) => {
        setFocusCell([row, col])
    };

    return (
        <>
            <FinancialDataInputBox p={2}>
                <TabArea>
                    <Grid container>
                        <Grid size={{ xs: 4, md: 3 }}>
                            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ "& .MuiTab-root.Mui-selected": { bgcolor: '#FFFFFF', color: "#1FB3A8" }, "& .MuiTabs-indicator": { bgcolor: "#FFFFFF" } }}>
                                <DataInputTab label="貸借対照表" value="BS" />
                                <span>&nbsp;&nbsp;</span>
                                <DataInputTab label="損益計算書" value="PL" />
                            </Tabs>
                        </Grid>
                        <Grid size={{ xs: 2, md: 5 }} />
                        <Grid size={{ xs: 3, md: 2 }}>
                            <div onClick={handleOpenSave} style={{ cursor: 'pointer' }}>
                                <img src={`${process.env.PUBLIC_URL}/Icons/saveIcon.svg`} width="45px" style={{ verticalAlign: '-45%' }} />入力データ保存
                            </div>
                        </Grid>
                        <Grid size={{ xs: 3, md: 2 }}>
                            <div onClick={handleOpenLoad} style={{ cursor: 'pointer' }}>
                                <img src={`${process.env.PUBLIC_URL}/Icons/loadIcon.svg`} width="57px" style={{ verticalAlign: '-45%' }} />保存データ読込
                            </div>
                        </Grid>
                    </Grid>
                </TabArea>
                <DataInputArea>
                    <Box p={3}>
                        <Box>
                            <Grid container>
                                <RadioGroup row value={selected} onChange={(e) => handleChangeRadio(e.target.value)} style={{ width: '100%', marginBottom: '1rem' }}>
                                    <Grid size={3}></Grid>
                                    <Grid size={3}>
                                        <Box key="radio1" display="flex" alignItems="center" sx={{ marginLeft: '1rem' }}>
                                            <FormControlLabel checked={state.tableValue["fiscalYM1"].isComparisonTarget} value="fiscalYM1" control={<Radio color='default' />} label="" sx={{ marginRight: '-0.5rem' }} />
                                            <DateInputForm value={state.tableValue["fiscalYM1"].fiscalYear || ""} size="small" slotProps={{ htmlInput: { style: { fontSize: '1rem', padding: '0.4rem' } } }} sx={{ width: '4rem', marginLeft: '1rem', marginRight: '0.5rem' }} onChange={(e) => handleChangeFiscalYear("fiscalYM1", e.target.value)} />
                                            <Box>年</Box>
                                            <DateInputForm value={state.tableValue["fiscalYM1"].fiscalMonth || ""} size="small" slotProps={{ htmlInput: { style: { fontSize: '1rem', padding: '0.4rem' } } }} sx={{ width: '2rem', marginLeft: '1rem', marginRight: '0.5rem' }} onChange={(e) => handleChangeFiscalMonth("fiscalYM1", e.target.value)} />
                                            <Box>月期</Box>
                                        </Box>
                                    </Grid>
                                    <Grid size={3}>
                                        <Box key="radio2" display="flex" alignItems="center" sx={{ marginLeft: '1rem' }}>
                                            <FormControlLabel checked={state.tableValue["fiscalYM2"].isComparisonTarget} value="fiscalYM2" control={<Radio color='default' />} label="" sx={{ marginRight: '-0.5rem' }} />
                                            <DateInputForm value={state.tableValue["fiscalYM2"].fiscalYear || ""} size="small" slotProps={{ htmlInput: { style: { fontSize: '1rem', padding: '0.4rem' } } }} sx={{ width: '4rem', marginLeft: '1rem', marginRight: '0.5rem' }} onChange={(e) => handleChangeFiscalYear("fiscalYM2", e.target.value)} />
                                            <Box>年</Box>
                                            <DateInputForm value={state.tableValue["fiscalYM2"].fiscalMonth || ""} size="small" slotProps={{ htmlInput: { style: { fontSize: '1rem', padding: '0.4rem' } } }} sx={{ width: '2rem', marginLeft: '1rem', marginRight: '0.5rem' }} onChange={(e) => handleChangeFiscalMonth("fiscalYM2", e.target.value)} />
                                            <Box>月期</Box>
                                        </Box>
                                    </Grid>
                                    <Grid size={3}>
                                        <Box key="radio3" display="flex" alignItems="center" sx={{ marginLeft: '1rem' }}>
                                            <FormControlLabel checked={state.tableValue["fiscalYM3"].isComparisonTarget} value="fiscalYM3" control={<Radio color='default' />} label="" sx={{ marginRight: '-0.5rem' }} />
                                            <DateInputForm value={state.tableValue["fiscalYM3"].fiscalYear || ""} size="small" slotProps={{ htmlInput: { style: { fontSize: '1rem', padding: '0.4rem' } } }} sx={{ width: '4rem', marginLeft: '1rem', marginRight: '0.5rem' }} onChange={(e) => handleChangeFiscalYear("fiscalYM3", e.target.value)} />
                                            <Box>年</Box>
                                            <DateInputForm value={state.tableValue["fiscalYM3"].fiscalMonth || ""} size="small" slotProps={{ htmlInput: { style: { fontSize: '1rem', padding: '0.4rem' } } }} sx={{ width: '2rem', marginLeft: '1rem', marginRight: '0.5rem' }} onChange={(e) => handleChangeFiscalMonth("fiscalYM3", e.target.value)} />
                                            <Box>月期</Box>
                                        </Box>
                                    </Grid>
                                </RadioGroup>
                            </Grid>
                        </Box>
                        <Table sx={{ borderCollapse: 'collapse', borderSpacing: '0' }}>
                            {items.map((row: any, index: any) => (
                                <TableRow key={index} sx={{ '& .MuiTableCell-root': { height: '0.3rem', fontSize: '0.8rem', paddingLeft: '0', paddingTop: '0', paddingBottom: '0', backgroundColor: '#FFFFFF' } }}>
                                    <Grid container>
                                        <Grid size={3}>
                                            {/* 表示する項目の階層に応じて空のセルを差し込む */}
                                            {row.level != 0 &&
                                                <TableCell sx={{ borderLeft: '1px solid', borderRight: '1px solid', borderTop: '1px solid transparent', borderBottom: '1px solid transparent', }} size='small' />
                                            }
                                            {row.level == 2 &&
                                                <TableCell sx={{ borderLeft: '1px solid', borderRight: '1px solid', borderTop: '1px solid transparent', borderBottom: '1px solid transparent', }} size='small' />
                                            }
                                            <DataInputTableCell
                                                sx={{
                                                    borderTop: '1px solid',
                                                    borderLeft: '1px solid',
                                                    borderRight: '1px solid',
                                                    borderBottom: index != items.length - 1 ? '1px solid transparent' : '1px solid',
                                                }}
                                                size='small'
                                                align='left'
                                            >
                                                {row.label}
                                            </DataInputTableCell>
                                        </Grid>
                                        <Grid size={3}>
                                            <DataInputTableCell align="right" size='small' sx={{ borderTop: '1px solid', borderRight: '1px solid', borderBottom: index != items.length - 1 ? '1px solid transparent' : '1px solid', paddingRight: '0.2rem' }}>
                                                <DataInputForm
                                                    ref={cellRefs[index][0]}
                                                    tabIndex={0}
                                                    onKeyDown={(e) => handleKeyDown(e, index, 0, cellRefs)}
                                                    type="text"
                                                    value={
                                                        tableValue.fiscalYM1[activeTab][row.key]
                                                            ? cellRefs[index][0].current == document.activeElement
                                                                ? tableValue.fiscalYM1[activeTab][row.key]
                                                                : tableValue.fiscalYM1[activeTab][row.key].toLocaleString()
                                                            : cellRefs[index][0].current == document.activeElement && tableValue.fiscalYM1[activeTab][row.key] !== "0"
                                                                ? ""
                                                                : 0
                                                    }
                                                    onFocus={() => handleFocus(index, 0)}
                                                    onBlur={(e) => { handleBlur("fiscalYM1", row.key, e.target.value); setFocusCell(null) }}
                                                    onChange={(e) => handleTableChange("fiscalYM1", row.key, e.target.value)}
                                                />
                                            </DataInputTableCell>
                                        </Grid>
                                        <Grid size={3}>
                                            <DataInputTableCell align="right" size='small' sx={{ borderTop: '1px solid', borderRight: '1px solid', borderBottom: index != items.length - 1 ? '1px solid transparent' : '1px solid', paddingRight: '0.2rem' }}>
                                                <DataInputForm
                                                    ref={cellRefs[index][1]}
                                                    tabIndex={0}
                                                    onKeyDown={(e) => handleKeyDown(e, index, 1, cellRefs)}
                                                    type="text"
                                                    value={
                                                        tableValue.fiscalYM2[activeTab][row.key]
                                                            ? cellRefs[index][1].current == document.activeElement
                                                                ? tableValue.fiscalYM2[activeTab][row.key]
                                                                : tableValue.fiscalYM2[activeTab][row.key].toLocaleString()
                                                            : cellRefs[index][1].current == document.activeElement && tableValue.fiscalYM2[activeTab][row.key] !== "0"
                                                                ? ""
                                                                : 0
                                                    }
                                                    onFocus={() => handleFocus(index, 1)}
                                                    onBlur={(e) => { handleBlur("fiscalYM2", row.key, e.target.value); setFocusCell(null) }}
                                                    onChange={(e) => handleTableChange("fiscalYM2", row.key, e.target.value)}
                                                />
                                            </DataInputTableCell>
                                        </Grid>
                                        <Grid size={3}>
                                            <DataInputTableCell align="right" size='small' sx={{ borderTop: '1px solid', borderRight: '1px solid', borderBottom: index != items.length - 1 ? '1px solid transparent' : '1px solid', paddingRight: '0.2rem' }}>
                                                <DataInputForm
                                                    ref={cellRefs[index][2]}
                                                    tabIndex={0}
                                                    onKeyDown={(e) => handleKeyDown(e, index, 2, cellRefs)}
                                                    type="text"
                                                    value={
                                                        tableValue.fiscalYM3[activeTab][row.key]
                                                            ? cellRefs[index][2].current == document.activeElement
                                                                ? tableValue.fiscalYM3[activeTab][row.key]
                                                                : tableValue.fiscalYM3[activeTab][row.key].toLocaleString()
                                                            : cellRefs[index][2].current == document.activeElement && tableValue.fiscalYM3[activeTab][row.key] !== "0"
                                                                ? ""
                                                                : 0
                                                    }
                                                    onFocus={() => handleFocus(index, 2)}
                                                    onBlur={(e) => { handleBlur("fiscalYM3", row.key, e.target.value); setFocusCell(null) }}
                                                    onChange={(e) => handleTableChange("fiscalYM3", row.key, e.target.value)}
                                                />
                                            </DataInputTableCell>
                                        </Grid>
                                    </Grid>
                                </TableRow>
                            ))}
                        </Table>
                    </Box>
                </DataInputArea>
            </FinancialDataInputBox>
            <SaveConditionModal isOpen={openSave} financialAnalysisData={state.tableValue} yearEdition={props.yearEdition} handleClose={() => setOpenSave(false)} />
            <LoadConditionModal isOpen={openLoad} yearEdition={props.yearEdition} handleClose={() => setOpenLoad(false)} handleLoad={handleDataLoad} />
        </>
    );
}

export default FinancialDataInputForm;
